export enum PlanTier {
  KANBAN = 'Kanban',
  PRO = 'Pro',
  ENTERPRISE = 'Enterprise'
}

export interface PlanConfig {
  tier: PlanTier;
  price: number;
  currency: string;
  isActive: boolean;
  description: string;
}

export interface PlanFeature {
  id: number;
  plan_tier: PlanTier;
  feature_key: string;
  is_enabled: boolean;
  limit_value: number;
}

export interface Coupon {
  id: string;
  code: string;
  discountPercent: number;
  planTier: PlanTier | 'All';
  expiryDate: string;
  isActive: boolean;
  isEmailRestricted?: boolean;      // NEW: Whether coupon is restricted to specific emails
  allowedEmails?: string[];         // NEW: List of emails allowed to use this coupon
  createdAt?: string;               // NEW: When coupon was created
}

export interface CouponRestriction {
  id?: number;
  couponId: string;
  email: string;
  createdAt?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  plan: PlanTier;
  subscriptionInterval?: 'month' | 'year' | null;
  nextBillingDate?: string | null;
  lastInvoicedDate?: string | null;
  currentCouponCode?: string | null;
  currentDiscountPercent?: number;
  avatarUrl?: string;
  phoneNumber?: string;
  integrations?: IntegrationChannel[];
  isMfaEnabled?: boolean;
  mfaMethod?: 'totp' | 'email_otp';
  isActive: boolean;
}

export interface PaymentConfig {
  stripePublicKey: string;
  stripeSecretKey: string;
  paypalClientId: string;
  paypalSecret: string;
  paypalDonationLink: string;
  upiDonationId: string;
}

export interface Project { id: string; userId: string; name: string; industry: string; processType: string; description?: string; createdAt: string; tasks: Task[]; projectType: string; duration?: string; }
export type TaskStatus = 'backlog' | 'todo' | 'in-progress' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high';
export interface Task { id: string; title: string; description?: string; status: TaskStatus; priority: TaskPriority; estimatedHours: number; assignee?: string; dueDate?: string; subtasks?: SubTask[]; attachments?: TaskLink[]; history?: TaskHistoryItem[]; tags?: string[]; projectId?: string; project_id?: string; }
export type SubTaskStatus = 'pending' | 'completed';
export interface SubTask { id: string; title: string; description?: string; status: SubTaskStatus; assignee?: string; dueDate?: string; completedDate?: string; createdAt?: string; link?: string; history?: SubTaskHistoryItem[]; priority?: 'low' | 'medium' | 'high'; isRisk?: boolean; }
export interface SubTaskHistoryItem { id: string; action: string; timestamp: string; }
export interface TaskLink { id: string; title: string; url: string; type: 'doc' | 'video' | 'blog' | 'other'; }
export interface TaskHistoryItem { id: string; userId?: string; userName: string; action: string; timestamp: string; }
export interface AppNotification { id: string; type: 'overdue' | 'deadline' | 'system'; message: string; context: string; date: string; projectId?: string; read: boolean; }
export interface IntegrationChannel { id: string; name: string; category: 'messaging' | 'ai' | 'other'; isConnected: boolean; configData?: any; }
export interface AuthProviderConfig { id: string; name: string; isEnabled: boolean; clientId?: string; clientSecret?: string; }
export interface FAQItem { id: number; category: string; question: string; answer: string; }
export interface CMSPage { slug: string; title: string; content: string; last_updated: string; }
export interface CRMLead { id: number; name: string; email: string; company?: string; phone?: string; status: 'new' | 'contacted' | 'qualified'; source: string; created_at: string; notes?: string; comments?: CRMComment[]; linked_tasks?: Reminder[]; linked_tickets?: SupportTicket[]; }
export interface CRMComment { id: number; lead_id: number; user_name?: string; content: string; created_at: string; }
export interface Reminder { id: number; user_id: string; title: string; description?: string; due_date: string; status: 'pending' | 'completed'; priority: 'low' | 'medium' | 'high'; created_at: string; }
export interface SupportTicket { id: string; userId: string; userEmail: string; subject: string; status: 'open' | 'in-progress' | 'resolved'; priority: 'low' | 'medium' | 'high'; messages: { sender: 'user' | 'admin', text: string, timestamp: string }[]; attachments?: { type: 'image' | 'log', name: string, data: string }[]; created_at: string; }
export interface SystemLog { 
  id: string; 
  level: 'info' | 'warning' | 'error' | 'critical'; 
  source: string; 
  message: string; 
  details?: string; 
  timestamp: string; 
  userId?: string; 
  userName?: string; 
}
export interface Invoice {
  id: string;
  user_id: string;
  stripe_invoice_id?: string;
  amount: number;
  status: 'paid' | 'open' | 'void';
  payment_method?: string;
  issued_date: string;
  description: string;
  pdf_url?: string;
  interval?: 'month' | 'year';
  couponCode?: string;
  discountPercent?: number;
  billing_name?: string;
  billing_address?: string;
  billing_vat?: string;
}

export interface UserBillingInfo {
  id?: string;
  userId: string;
  name: string;
  address: string;
  vatGstId?: string;
  paymentMethod: string;
  paymentEmail?: string;
}

export interface PendingInvite {
  id: number;
  invited_email: string;
  invited_by_user_id: number;
  project_id: number;
  task_id?: number;
  coupon_code?: string;
  task_title?: string;
  project_name: string;
  inviter_name: string;
  inviter_email: string;
  invite_token: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expires_at?: string;
  accepted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface InviteStats {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  expired: number;
}

export interface PendingPlanChange { token: string; userId: string; newPlan: PlanTier; newRole: string; timestamp: string; }
export interface ProjectInputs { industry: string; processType: string; duration: string; projectType: string; }
export interface AITaskResponse { projectName: string; tasks: Task[]; risks?: string[]; description?: string; }
export interface TaskAnalysis {
  improvedDescription: string;
  suggestedSubtasks: Array<string | SuggestedSubtask>;
  risks: string[];
}
export interface SuggestedSubtask {
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  isRisk?: boolean;
}
export interface ProjectAIAnalytics {
  sentiment: string;
  sentimentScore?: number;
  criticalPath: string[];
  risks: string[];
  mitigations: string[];
  actions: string[];
  summary: string;
}

// Methodology Management
export interface Methodology {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface MethodologySettings {
  id: string;
  enableUserSelection: boolean;
  defaultMethodology: string; // methodology name
  availableMethodologies: string[]; // array of methodology names
  updatedAt: string;
}